import type { OfflineDeviceLink } from "@/lib/offline/db";

/** Which of the login route's four screens should render. */
export type LoginScreen = "loading" | "desktop-form" | "device-setup" | "pin";

export interface LoginScreenInput {
  /** Whether the device-setup / PIN flow is switched on for this environment. */
  deviceLinkingEnabled: boolean;
  /** True while the saved device link is still being read from IndexedDB. */
  isLoadingLink: boolean;
  /** The saved link, once read. `undefined` means this device isn't linked. */
  link: OfflineDeviceLink | undefined;
  /** Whether the primary pointer is coarse (a phone or tablet). */
  isTouchDevice: boolean;
}

/**
 * Decide which login screen to show.
 *
 * A saved, PIN-enabled link wins over the pointer type. That ordering matters
 * for two reasons: it keeps the decision independent of `matchMedia`, which can
 * report a fine pointer on the first frame and flip a moment later — briefly
 * painting the email/password form on a device that should go straight to PIN
 * entry — and it does the right thing for a tablet driven by a trackpad
 * keyboard, which genuinely reports a fine pointer for as long as it's attached.
 *
 * The pointer check still decides what an *unlinked* device sees, since only a
 * touch device should be offered the shared-device setup screen.
 */
export function resolveLoginScreen({
  deviceLinkingEnabled,
  isLoadingLink,
  link,
  isTouchDevice,
}: LoginScreenInput): LoginScreen {
  if (!deviceLinkingEnabled) {
    return "desktop-form";
  }

  if (isLoadingLink) {
    return "loading";
  }

  // A link saved without a device secret predates the credential and can no
  // longer talk to the server, so it is treated as no link at all: the device
  // goes through setup once and comes back with a working one.
  if (link?.deviceSecret) {
    // A linked device whose nursery has PIN login turned off falls back to the
    // standard form, so there is always a working sign-in. The PIN screen
    // returns once the nursery re-enables PIN and the device re-links.
    return link.pinLoginEnabled ? "pin" : "desktop-form";
  }

  return isTouchDevice ? "device-setup" : "desktop-form";
}
